import { Component, OnChanges, Input, Output } from 'angular-ts-decorators';
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from '@fortawesome/pro-solid-svg-icons';
import { map, forEach, pullAll, concat, includes } from 'lodash';
/**
 * A component to chose multiple elements form the list.
 * # HTML Example
 * ```HTML
 * <sw-multi-select model="$ctrl.model" options="$ctrl.options" on-change="$ctrl.onChange(option)" saturated="$ctrl.saturated"></sw-multi-select>
 * ```
 *
 * where
 * * options is a list of available options as objects or strings
 * * model is a list o picked up options
 * * onChange is a callback that is called everytime when value is changed
 * * formatLabel is a function that is called on each value to produce a label
 * * getKey is a function that is called on each value to produce a value that is set to model
 * * saturated is a boolean, if true it disables checkbox input. Optionally,
 * the parent component can set a number of allowed checked boxes before the
 * rest become disabled.
 *
 * # Readonly Example
 * ```HTML
 * <sw-multi-select model="$ctrl.selectedCheckboxes" saturated="$ctrl.saturated"></sw-multi-select>
 * ```
 *
 * # Controller
 * ```JS
 * // when the user has selected 5 checkboxes, the rest become disabled
 * $ctrl.saturated = $ctrl.selectedCheckboxes.length >= 5;
 * ```
 */
@Component({
  selector: 'swMultiSelect',
  template: require('./sw-multi-select.html').default,
})
export class SwMultiSelectComponent implements OnChanges {
  @Input() options: any;
  @Input('=?') model: any;
  @Output() onChange: () => any;
  @Input() formatLabel: any;
  @Input() getKey: any;
  @Input() saturated: any;
  @Input('@saturated') saturatedAttr: any;

  moreCollapsed: boolean = true;

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faMinus = faMinus;

  // based on what looks good on mobile
  simpleListThreshold: number = 5;
  searchThreshold: number = this.simpleListThreshold;
  limitThreshold: number = this.simpleListThreshold;
  limit: number = this.simpleListThreshold;
  tooLongThreshold: number = 50;

  search: { label?: string };
  processedOptions: {
    value: string;
    label: string;
    checked: boolean;
  }[];
  filteredLength: number;

  constructor(
    private $timeout: ng.ITimeoutService,
    private filterFilter: ng.IFilterFilter,
  ) {
    'ngInject';

    this.search = {};
  }

  ngOnChanges() {
    if (!angular.isFunction(this.formatLabel)) {
      this.formatLabel = (x) => x;
    }

    if (!angular.isFunction(this.getKey)) {
      this.getKey = (x) => x;
    }

    if (
      angular.isArray(this.options) &&
      this.options.length <= this.searchThreshold
    ) {
      this.search.label = '';
    }

    this.processedOptions = map(this.options, (value) => {
      return {
        value: this.getKey(value),
        label: this.formatLabel(value),
        checked: includes(this.model, value),
      };
    });

    this.setFilteredLength();
  }

  toggleLimit() {
    if (angular.isNumber(this.limit)) {
      this.limit = undefined;
    } else {
      this.limit = this.simpleListThreshold;
    }
  }

  selectAll() {
    const filterOptions = this.filterFilter(this.processedOptions, this.search);

    forEach(filterOptions, (value) => {
      value.checked = true;
    });

    if (angular.isUndefined(this.model)) {
      this.model = [];
    }

    const pickedValues = map(filterOptions, 'value');

    this.model = concat(this.model, pullAll(pickedValues, this.model));

    this.wrappedCheck();
  }

  unselectAll() {
    forEach(this.processedOptions, (value) => {
      value.checked = false;
    });

    if (angular.isArray(this.model)) {
      this.model.length = 0;
    }
  }

  getLabelClass(option) {
    return {
      'sw-multi-select__wrapper': true,
      'sw-multi-select__wrapper--saturated': this.saturated && !option.checked,
      'sw-multi-select__wrapper--active': option.checked,
    };
  }

  // TODO unit tests
  wrappedCheck() {
    // delay the call, so the model is updated before the callback is run
    this.$timeout(() => {
      this.onChange();
    });
  }

  // TODO unit test
  setFilteredLength() {
    this.filteredLength = this.filterFilter(
      this.processedOptions,
      this.search,
    ).length;
  }
}
